import { useCallback, useEffect, useRef } from "react";

// Cloudflare Turnstile.
//
// The widget solves itself quietly when the page loads and hands us a token via
// its callback. Submitting a form just reads that token. This is deliberately
// simpler than Turnstile's execute-on-submit mode, which needs a reset/execute
// dance and gave us a promise that never resolved.
//
// "interaction-only" appearance means a normal visitor sees nothing at all — a
// challenge is only drawn if Cloudflare decides the request looks suspicious.
//
// If REACT_APP_TURNSTILE_SITE_KEY is unset at build time the hook reports itself
// unconfigured and the forms carry on working without it.

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const TOKEN_TIMEOUT_MS = 20000;

let scriptPromise = null;

function loadTurnstileScript() {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const el = document.createElement("script");
      el.src = SCRIPT_SRC;
      el.async = true;
      el.defer = true;
      el.onload = () => resolve();
      el.onerror = () => {
        scriptPromise = null;
        reject(new Error("Turnstile script failed to load"));
      };
      document.head.appendChild(el);
    });
  }
  return scriptPromise;
}

export function useTurnstile() {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const tokenRef = useRef("");
  const waitersRef = useRef([]);

  // Hand a token (or "") to anyone currently waiting, and remember it.
  const deliver = useCallback((token) => {
    tokenRef.current = token || "";
    const waiting = waitersRef.current;
    waitersRef.current = [];
    waiting.forEach((fn) => fn(tokenRef.current));
  }, []);

  useEffect(() => {
    if (!SITE_KEY) return undefined;
    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || widgetIdRef.current !== null) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          appearance: "interaction-only",
          theme: "dark",
          callback: (token) => deliver(token),
          "error-callback": () => deliver(""),
          "timeout-callback": () => deliver(""),
          "expired-callback": () => {
            tokenRef.current = "";
            try {
              window.turnstile.reset(widgetIdRef.current);
            } catch {
              /* widget gone */
            }
          },
        });
      })
      .catch(() => {
        // Script blocked or offline; getToken() resolves "" and the caller decides.
      });

    return () => {
      cancelled = true;
      deliver("");
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* already removed */
        }
        widgetIdRef.current = null;
      }
    };
  }, [deliver]);

  // The token if we already have one, otherwise wait for the callback.
  const getToken = useCallback(() => {
    if (!SITE_KEY) return Promise.resolve("");
    if (tokenRef.current) return Promise.resolve(tokenRef.current);
    return new Promise((resolve) => {
      let onDone;
      const timer = setTimeout(() => {
        waitersRef.current = waitersRef.current.filter((fn) => fn !== onDone);
        resolve("");
      }, TOKEN_TIMEOUT_MS);
      onDone = (token) => {
        clearTimeout(timer);
        resolve(token);
      };
      waitersRef.current.push(onDone);
    });
  }, []);

  // Tokens are single use. Call after a successful submission so the next one
  // gets a fresh token rather than replaying a spent one.
  const resetToken = useCallback(() => {
    tokenRef.current = "";
    if (widgetIdRef.current !== null && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
      } catch {
        /* widget gone */
      }
    }
  }, []);

  return { containerRef, getToken, resetToken, configured: Boolean(SITE_KEY) };
}
